import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button, TextField } from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-github';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

function FolderTree({ path, onSelect, selectedPath }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/templates-list?path=${encodeURIComponent(path)}`)
      .then(res => res.json())
      .then(data => {
        setItems(data);
        setLoading(false);
      });
  }, [path]);

  const handleExpand = (item) => {
    setExpanded(e => ({ ...e, [item.path]: !e[item.path] }));
  };

  return (
    <Box sx={{ ml: path ? 2 : 0 }}>
      {loading && <CircularProgress size={16} />}
      {items.map(item => (
        <Box key={item.path || item.name}>
          {item.type === 'dir' ? (
            <>
              <Button size="small" onClick={() => handleExpand(item)}>{expanded[item.path] ? '-' : '+'}</Button>
              <span style={{ fontWeight: 'bold' }}>{item.name}</span>
              {expanded[item.path] && (
                <FolderTree path={item.path || item.name} onSelect={onSelect} selectedPath={selectedPath} />
              )}
            </>
          ) : (
            <Button
              size="small"
              variant={selectedPath === item.path ? 'contained' : 'outlined'}
              sx={{ my: 0.5 }}
              onClick={() => onSelect(item.path || item.name)}
            >
              {item.name}
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function TemplatesAdminPage() {
  const [selectedPath, setSelectedPath] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [commitMsg, setCommitMsg] = useState('Update template');

  useEffect(() => {
    if (selectedPath) {
      setLoading(true);
      fetch(`${API_BASE_URL}/api/templates-file?path=${encodeURIComponent(selectedPath)}`)
        .then(res => res.json())
        .then(data => {
          setFileContent(data.content || '');
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to fetch file content');
          setLoading(false);
        });
    }
  }, [selectedPath]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/templates-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: selectedPath, content: fileContent, message: commitMsg })
      });
      const data = await res.json();
      if (data.success) {
        alert('File saved successfully!');
      } else {
        setError(data.error || 'Failed to save file');
      }
    } catch (err) {
      setError('Failed to save file');
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Templates Admin</Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ minWidth: 300 }}>
          <Typography variant="subtitle1">Folders & Files</Typography>
          <FolderTree path="" onSelect={setSelectedPath} selectedPath={selectedPath} />
        </Box>
        <Box sx={{ flex: 1 }}>
          {loading && <CircularProgress />}
          {error && <Typography color="error">{error}</Typography>}
          {selectedPath && !loading && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{selectedPath}</Typography>
              <AceEditor
                mode="yaml"
                theme="github"
                value={fileContent}
                onChange={setFileContent}
                name="yaml-template-editor"
                editorProps={{ $blockScrolling: true }}
                width="100%"
                height="400px"
              />
              <TextField
                label="Commit Message"
                value={commitMsg}
                onChange={e => setCommitMsg(e.target.value)}
                fullWidth
                sx={{ mt: 2 }}
              />
              <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
                Save Changes
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
